import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useAtom } from 'jotai';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  endpointFundValuationEventDELETE,
  endpointFundValuationEventPOST,
  FundValuationEventPayload,
} from '~/api/def/endpoints/fundAnalyticsEndpoint';
import { FundGetData } from '~/api/def/endpoints/fundEndpoint';
import { toastAtom } from '~/atoms/toastAtom';
import { FundValuationEventTable } from '~/components/ui/tables/ag/fundValuationEvent/fundValuationEventTable';
import { currencyList } from '~/config/currenciesStatic';

export const FundValuationEventSchema = z.object({
  valuationDate: z
    .date({
      required_error: 'Date is required',
      invalid_type_error: 'Date is required',
    })
    .refine((date) => date <= new Date(), {
      message: 'Date cannot be in the future',
    })
    .transform((date) => format(new Date(date), 'yyyy-MM-dd')),
  value: z
    .number({
      required_error: 'Amount is required',
      invalid_type_error: 'Amount is required',
    })
    .nonnegative('Amount must be a non-negative number'),
  currency: z.string({
    required_error: 'Currency is required',
    invalid_type_error: 'Currency is required',
  }),
});

type ValidationSchema = z.infer<typeof FundValuationEventSchema>;

interface FundValuationEventProps {
  selectedFund: FundGetData;
  onHide: () => void;
  refetchFundListAnalytics: () => void;
}

export function FundValuationEventSection({
  selectedFund,
  onHide,
  refetchFundListAnalytics,
}: FundValuationEventProps) {
  const [toastCurrent] = useAtom<Toast | null>(toastAtom);

  const { mutateAsync: deleteValuationEventMutateAsync } = useMutation({
    mutationFn: ({
      fundId,
      valuationEventId,
    }: {
      fundId: string;
      valuationEventId: string;
    }) => {
      return endpointFundValuationEventDELETE(fundId, valuationEventId);
    },
    onSuccess: () => {
      toastCurrent?.show({
        severity: 'success',
        detail: 'Valuation event deleted successfully.',
      });
      refetchFundListAnalytics();
    },
    onError: () => {
      toastCurrent?.show({
        severity: 'error',
        detail: 'Something went wrong while deleting valuation event.',
      });
    },
  });

  const { mutateAsync: createValuationEventMutateAsync, isPending } =
    useMutation({
      mutationFn: (data: FundValuationEventPayload) => {
        return endpointFundValuationEventPOST(selectedFund.id, data);
      },
      onSuccess: () => {
        toastCurrent?.show({
          severity: 'success',
          detail: 'Valuation event added successfully.',
        });
        refetchFundListAnalytics();
      },
      onError: () => {
        toastCurrent?.show({
          severity: 'error',
          detail: 'Something went wrong while adding valuation event.',
        });
      },
      onSettled: () => {
        reset();
      },
    });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ValidationSchema>({
    resolver: zodResolver(FundValuationEventSchema),
    defaultValues: {
      valuationDate: undefined,
      value: undefined,
      currency: selectedFund.currency,
    },
  });

  const onSubmit = async (data: ValidationSchema) => {
    await createValuationEventMutateAsync(data);
  };

  const confirmDeleteDialog = (valuationEventId: string) => {
    confirmDialog({
      message: 'Do you want to delete this record?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      accept: () => handleRemoveValuationEvent(valuationEventId),
    });
  };

  const handleRemoveValuationEvent = async (valuationEventId: string) => {
    await deleteValuationEventMutateAsync({
      fundId: selectedFund.id,
      valuationEventId: valuationEventId,
    });
  };

  return (
    <div className="flex flex-column gap-2">
      <ConfirmDialog />
      <div className="p-3">
        <div style={{ height: '16.3rem' }}>
          <FundValuationEventTable
            loading={false}
            rowData={(selectedFund.fundValuationEvents || []).map((event) => ({
              ...event,
              handleRemove: () => confirmDeleteDialog(event.id),
            }))}
          />
        </div>
        <form>
          <div className="grid pt-3">
            <div className="col-6 flex flex-column gap-1">
              <label htmlFor="valuationDate">Date</label>
              <Controller
                name="valuationDate"
                control={control}
                render={({ field }) => (
                  <Calendar
                    {...field}
                    id="valuationDate"
                    showIcon
                    value={field.value ? new Date(field.value) : null}
                  />
                )}
              />
              {errors.valuationDate && (
                <small className="p-error">
                  {errors.valuationDate.message}
                </small>
              )}
            </div>
            <div className="col-3 flex flex-column gap-1">
              <label htmlFor="value">Amount</label>
              <Controller
                name="value"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    id="value"
                    onChange={(e) => field.onChange(e.value)}
                  />
                )}
              />
              {errors.value && (
                <small className="p-error">{errors.value.message}</small>
              )}
            </div>
            <div className="col-3 flex flex-column gap-1">
              <label htmlFor="currency">Currency</label>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    id="currency"
                    filter
                    {...field}
                    options={currencyList}
                    pt={{
                      panel: {
                        style: { width: '2rem' },
                      },
                    }}
                  />
                )}
              />
              {errors.currency && (
                <small className="p-error">{errors.currency.message}</small>
              )}
            </div>
          </div>
        </form>
      </div>
      <div
        className="flex justify-content-end p-3 gap-2"
        style={{
          borderTop: '1px solid #e9ecef',
        }}
      >
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={() => onHide()}
          className="p-button-text"
        />
        <Button
          type="submit"
          label="Add"
          icon={isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
          disabled={isPending}
          onClick={handleSubmit(onSubmit)}
        />
        <Button
          type="submit"
          label="Add and Close"
          icon={isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
          disabled={isPending}
          onClick={handleSubmit(async (data) => {
            await onSubmit(data);
            onHide();
          })}
        />
      </div>
    </div>
  );
}
